/**
 * cart-addons.js
 * Maneja:
 *  1. Rotación automática/manual del banner de promociones
 *  2. Interceptar "Comprar ahora" para mostrar la pantalla de add-ons antes del checkout real
 *  3. Agregar/quitar los add-ons seleccionados (sin duplicar si ya estaban en el carrito)
 *
 * Requiere:
 *  - snippets/cart-promo-banner.liquid
 *  - snippets/cart-addons-step.liquid
 *  - que el formulario del carrito conserve el id "CartDrawer-Form" (Dawn estándar)
 *
 * Bijou (rendimiento):
 *  - Antes, cada vez que el carrito se volvía a dibujar se creaba un banner
 *    nuevo con su propio setInterval, y los anteriores nunca se detenían: los
 *    temporizadores se acumulaban durante la visita. Ahora hay uno solo.
 *  - El banner solo rota cuando está visible (cajón abierto) y la pestaña
 *    está activa.
 *  - Si falla una petición al agregar o quitar un add-on, la casilla regresa
 *    a su estado anterior en lugar de quedar desincronizada.
 */

(function () {
  const PROMO_ROTATE_MS = 4000;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  function qsa(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  // ---------- Banner de promociones ----------

  let promoBanner = null;
  let promoSlides = [];
  let promoCurrent = 0;
  let promoTimer = null;
  let promoObserver = null;
  let promoVisible = true;

  function stopPromoBanner() {
    clearInterval(promoTimer);
    promoTimer = null;
    if (promoObserver) {
      promoObserver.disconnect();
      promoObserver = null;
    }
    promoBanner = null;
    promoSlides = [];
  }

  function showSlide(index) {
    promoSlides.forEach((s, i) => s.classList.toggle('is-active', i === index));
    promoCurrent = index;
  }

  function nextSlide() {
    if (!promoSlides.length) return;
    showSlide((promoCurrent + 1) % promoSlides.length);
  }

  function tickPromo() {
    // El carrito se volvió a dibujar y este banner ya no existe
    if (!promoBanner || !promoBanner.isConnected) {
      stopPromoBanner();
      return;
    }
    if (document.hidden || !promoVisible) return;
    nextSlide();
  }

  function startPromoTimer() {
    clearInterval(promoTimer);
    promoTimer = setInterval(tickPromo, PROMO_ROTATE_MS);
  }

  function initPromoBanner() {
    const banner = qs('[data-cart-promo-banner]');

    // Mismo banner que ya está corriendo: no hacer nada
    if (banner && banner === promoBanner) return;

    stopPromoBanner();
    if (!banner) return;

    const slides = qsa('[data-promo-slide]', banner);
    if (slides.length <= 1) return;

    promoBanner = banner;
    promoSlides = slides;
    const activeIndex = slides.findIndex((s) => s.classList.contains('is-active'));
    promoCurrent = activeIndex === -1 ? 0 : activeIndex;

    // Solo rota mientras el banner esté en pantalla (con el cajón cerrado, el
    // banner queda fuera de la vista y el temporizador no hace trabajo)
    promoVisible = true;
    if ('IntersectionObserver' in window) {
      promoObserver = new IntersectionObserver((entries) => {
        promoVisible = entries[0].isIntersecting;
      });
      promoObserver.observe(banner);
    }

    const nextBtn = qs('[data-promo-next]', banner);
    if (nextBtn && !nextBtn.dataset.promoBound) {
      nextBtn.dataset.promoBound = 'true';
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        nextSlide();
        startPromoTimer();
      });
    }

    startPromoTimer();
  }

  // ---------- Pantalla de add-ons ----------

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('No se pudo leer el carrito');
    return res.json();
  }

  function findCartItem(cart, variantId) {
    return cart.items.find((item) => String(item.variant_id) === String(variantId));
  }

  async function addAddon(variantId) {
    // Bijou: evita duplicados — si el add-on ya está en el carrito
    // (por ejemplo porque el usuario regresó a navegar y volvió a marcar
    // la casilla), no se vuelve a agregar otra unidad.
    const cart = await fetchCart();
    if (findCartItem(cart, variantId)) {
      return cart;
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    if (!res.ok) throw new Error('No se pudo agregar el add-on');
    return res.json();
  }

  async function removeAddon(variantId) {
    const cart = await fetchCart();
    const item = findCartItem(cart, variantId);
    if (!item) return cart;
    // Bijou: se usa la "key" de la línea en vez del número de línea, así se
    // quita exactamente ese producto aunque el orden del carrito cambie.
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 }),
    });
    if (!res.ok) throw new Error('No se pudo quitar el add-on');
    return res.json();
  }

  // Bijou: al abrir la pantalla de add-ons, refleja en las casillas si ese
  // producto ya está en el carrito (por ejemplo, si el usuario ya lo había
  // agregado en un intento anterior de pagar y regresó a navegar).
  async function syncAddonCheckboxes() {
    const step = qs('[data-cart-addons-step]');
    if (!step) return;
    const checkboxes = qsa('[data-addon-checkbox]', step);
    if (!checkboxes.length) return;
    try {
      const cart = await fetchCart();
      checkboxes.forEach((checkbox) => {
        checkbox.checked = Boolean(findCartItem(cart, checkbox.dataset.variantId));
      });
    } catch (err) {
      // Si falla la lectura del carrito, las casillas se quedan como estaban
    }
  }

  function openAddonsStep() {
    const step = qs('[data-cart-addons-step]');
    const mainView = qs('#CartDrawer-CartItems');
    const footer = qs('.drawer__footer');
    if (!step) return;
    step.hidden = false;
    if (mainView) mainView.style.display = 'none';
    if (footer) footer.style.display = 'none';
    syncAddonCheckboxes();
  }

  function closeAddonsStep() {
    const step = qs('[data-cart-addons-step]');
    const mainView = qs('#CartDrawer-CartItems');
    const footer = qs('.drawer__footer');
    if (!step) return;
    step.hidden = true;
    if (mainView) mainView.style.display = '';
    if (footer) footer.style.display = '';
  }

  function initCheckoutIntercept() {
    document.addEventListener('submit', function (e) {
      const form = e.target.closest('#CartDrawer-Form');
      if (!form) return;

      if (form.dataset.addonsConfirmed === 'true') {
        // El usuario ya pasó por la pantalla de add-ons, dejar continuar a checkout real
        return;
      }
      e.preventDefault();
      openAddonsStep();
    });
  }

  function initAddonsInteractions() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-addons-back]')) {
        closeAddonsStep();
        return;
      }

      if (e.target.closest('[data-addons-continue]')) {
        const form = qs('#CartDrawer-Form');
        if (form) form.dataset.addonsConfirmed = 'true';
        // Bijou: en vez de reenviar el formulario (que en este tema termina
        // aterrizando en /cart antes de /checkout), navegamos directo. Los
        // add-ons ya quedaron agregados al carrito en tiempo real al marcar
        // cada casilla, así que no se pierde nada.
        window.location.href = '/checkout';
      }
    });

    document.addEventListener('change', function (e) {
      const checkbox = e.target.closest('[data-addon-checkbox]');
      if (!checkbox) return;

      const variantId = checkbox.dataset.variantId;
      const wantsAddon = checkbox.checked;
      checkbox.disabled = true;

      const action = wantsAddon ? addAddon(variantId) : removeAddon(variantId);
      action
        .then(() => {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        })
        .catch(() => {
          // Regresa la casilla a su estado anterior si la petición falló
          checkbox.checked = !wantsAddon;
        })
        .finally(() => {
          checkbox.disabled = false;
        });
    });
  }

  onReady(function () {
    initPromoBanner();
    initCheckoutIntercept();
    initAddonsInteractions();
  });

  document.addEventListener('cart:updated', initPromoBanner);
  document.addEventListener('cart:refresh', initPromoBanner);

  window.BijouCartAddons = { openAddonsStep, closeAddonsStep, initPromoBanner, syncAddonCheckboxes };
})();